import axios from "axios";
import { API_URL, IS_APP_STUDYLESS } from "../constants.js";

/**
 * @param {Response} resp
 * @returns {Promise<{data: null, error: {code: Number, text: string, body: Object}}|{data: Object, error: null}>}
 */
async function parseResponse(resp) {
  if (resp.ok) {
    const contentType = resp.headers.get("Content-Type");
    if (!contentType || contentType.contains("application/json")) {
      return {
        data: await resp.json(),
        error: null,
      };
    } else {
      return { data: await resp.text(), error: null };
    }
  } else {
    const response = {
      data: null,
      error: {
        code: resp.status,
        text: resp.statusText,
        body: await resp.json(),
      },
    };
    if (resp.status >= 400 && resp.status < 500) {
      response.error.text = response.error.body.message;
    }
    return response;
  }
}

/**
 * @param {String} locale
 * @param {Object} headers
 * @param {Boolean} [isFormData]
 * @param {Boolean} [appendContentType]
 * @returns {HeadersInit}
 */
const makeHeaders = (locale, headers, isFormData, appendContentType = true) => {
  const out = new Headers({
    Accept: "application/json",
    device: "Browser/WebApp/1/0.0.1/1.0.12",
    "company-id": IS_APP_STUDYLESS ? "1772" : "-1",
    locale,
    timezoneoffset: -new Date(Date.now()).getTimezoneOffset() * 60 * 1000,
    ...headers,
  });
  if (!isFormData && appendContentType) {
    out.append("Content-Type", "application/json");
  }
  return out;
};

/**
 * @param {String} url
 * @param {String} locale
 * @param {Object} headers
 * @returns {Promise<{data: null, error: {code: Number, text: string, body: Object}}|{data: Object, error: null}>}
 */
export async function get(url, locale, headers = {}) {
  const resp = await fetch(`${API_URL}/${url}`, {
    method: "GET",
    headers: makeHeaders(locale, headers),
  });
  return await parseResponse(resp);
}

/**
 * @param {String} url
 * @param {String} locale
 * @param {Object} postData
 * @param {Object} headers
 * @returns {Promise<{data: null, error: {code: Number, text: string, body: Object}}|{data: Object, error: null}>}
 */
export async function post(url, locale, postData, headers = {}) {
  const isFormData = postData instanceof FormData;
  const resp = await fetch(`${API_URL}/${url}`, {
    method: "POST",
    body: isFormData ? postData : JSON.stringify(postData),
    headers: makeHeaders(locale, headers, isFormData),
  });
  return await parseResponse(resp);
}

/**
 * Выполняет POST-запрос с отслеживанием реального прогресса загрузки (upload only).
 * Использует axios с onUploadProgress — корректно сообщает фактический прогресс
 * передачи данных по сети (в отличие от сырого XHR, который буферизует FormData).
 *
 * @param {String} url - Адрес эндпоинта (без базового URL).
 * @param {String} locale - Текущая локаль.
 * @param {FormData} formData - Тело запроса (FormData с файлами).
 * @param {Function} [onProgress] - Колбэк прогресса загрузки, принимает число 0-100.
 * @param {Object} headers - Дополнительные заголовки.
 * @returns {Promise<{data: null, error: {code: Number, text: string, body: Object}}|{data: Object, error: null}>}
 */
export async function postWithProgress(url, locale, formData, onProgress, headers = {}) {
  const isFormData = formData instanceof FormData;
  const hdrs = makeHeaders(locale, headers, isFormData);
  // axios ожидает плоский объект заголовков, а не Headers
  const axiosHeaders = Object.fromEntries(hdrs.entries());

  try {
    const response = await axios({
      url: `${API_URL}/${url}`,
      method: "POST",
      headers: axiosHeaders,
      data: formData,
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          onProgress(Math.round((progressEvent.loaded / progressEvent.total) * 100));
        }
      },
    });
    return { data: response.data, error: null };
  } catch (/** @type {any} */ err) {
    if (err.response) {
      const { status, statusText, data } = err.response;
      const error = { code: status, text: statusText, body: data };
      if (status >= 400 && status < 500 && data && data.message) {
        error.text = data.message;
      }
      return { data: null, error };
    }
    return { data: null, error: { code: 0, text: err.toString(), body: null } };
  }
}

/**
 * @param {String} url
 * @param {String} locale
 * @param {Object} postData
 * @param {Object} headers
 * @returns {Promise<{data: null, error: {code: Number, text: string, body: Object}}|{data: Object, error: null}>}
 */
export async function patch(url, locale, postData, headers = {}) {
  const resp = await fetch(`${API_URL}/${url}`, {
    method: "PATCH",
    body: JSON.stringify(postData),
    headers: makeHeaders(locale, headers),
  });
  return await parseResponse(resp);
}

/**
 * @param {String} url
 * @param {String} locale
 * @param {Object} postData
 * @param {Object} headers
 * @returns {Promise<{data: null, error: {code: Number, text: string, body: Object}}|{data: Object, error: null}>}
 */
export async function put(url, locale, postData, headers = {}) {
  const isFormData = postData instanceof FormData;
  const options = {
    method: "PUT",
    headers: makeHeaders(locale, headers, isFormData, !!postData),
  };
  if (postData) {
    options.body = JSON.stringify(postData);
  }
  const resp = await fetch(`${API_URL}/${url}`, options);
  return await parseResponse(resp);
}

/**
 * @param {String} url
 * @param {String} locale
 * @param {Object} headers
 * @returns {Promise<{data: null, error: {code: Number, text: string, body: Object}}|{data: Object, error: null}>}
 */
export async function del(url, locale, headers = {}) {
  const resp = await fetch(`${API_URL}/${url}`, {
    method: "DELETE",
    headers: makeHeaders(locale, headers, false, false),
  });
  const retval = await parseResponse(resp);
  return retval;
}
